import React, { useCallback, useState } from 'react';
import { useNavigation } from '@react-navigation/native';
import { useAsyncCallback } from 'react-async-hook';
import { Menu } from 'react-native-paper';
import _ from 'lodash';

import Button, { LinkButton } from '@appComponents/Button';
import { useMenuState } from '@appComponents/Menu';
import { Box, ScreenLoader } from '@appComponents/ScreenLayout';
import ArchiveDialog from './UserDetails/ArchiveDialog';
import {
  useCompanyData,
  useCompanyUsers,
  useCompanyUsersFull,
  archiveOrDeleteUser,
  restoreUser,
} from '@appUtils/api';
import { getRoleString } from '@appUtils/user';
import { useUserAircraft } from '@appUtils/aircraft';
import { UserRole } from '@appUtils/tripConverter';
import { Cell, PhoneCell, PlainCell, UsersTable } from './TableCells';
import UserRoleIndicator from '@appComponents/UserRoleIndicator';
import { getUid } from '@appFirebase';

const UserSupplier = ({ company, roles, users, setUsers, archived }) => {
  const { data } = useCompanyUsersFull({
    roles,
    company,
    archived,
  });
  if (data && !_.isEqual(data, users)) {
    setUsers(data);
  }

  return null;
};

const UserList = ({
  role = UserRole.OWNER,
  withActions = false,
  showArchived = false,
}: UserListProps) => {
  const [company, companyLoading] = useCompanyData();
  const [users, setUsers] = useState([]);
  const [owners, ownersLoading] = useCompanyUsers(UserRole.OWNER);
  const waitForOwners = role === UserRole.PASSENGER && ownersLoading;
  const roles =
    role === UserRole.MANAGER ? [UserRole.MANAGER, UserRole.EMPLOYEE] : [role];

  const [isArchiving, setIsArchiving] = useState(false);
  const [archiveMode, setArchiveMode] = useState('archive');
  const [selectedUser, setSelectedUser] = useState(null);
  const { data: aircraft = [] } = useUserAircraft({
    id: selectedUser?.id,
    role: selectedUser?.role,
  });

  const onConfirmArchive = async () => {
    if (!selectedUser) {
      return;
    }

    try {
      setIsArchiving(false);

      if (archiveMode === 'restore') {
        await restoreUser(selectedUser.id);
      } else {
        await archiveOrDeleteUser(selectedUser, aircraft);
      }
    } catch (error) {
      console.error(`Error during ${archiveMode}:`, error);
    }
  };

  if (!company?.id || companyLoading || waitForOwners) {
    return <ScreenLoader />;
  }

  return (
    <>
      <UserSupplier
        company={company}
        roles={roles}
        users={users}
        setUsers={setUsers}
        archived={showArchived}
      />
      <UsersTable list={users} listType={`${getRoleString(role)}s`}>
        <NameLinkCell title="Name" flex={30} />
        <PlainCell title="Email" path="email" flex={30} />
        <PhoneCell title="Phone" flex={15} />
        {role === UserRole.PASSENGER && (
          <OwnersCell title="Clients" owners={owners} flex={15} />
        )}
        {role !== UserRole.PASSENGER && (
          <AircraftCell title="Aircraft" role={role} flex={15} />
        )}
        <WeightCell title="Weight" flex={5} />
        {withActions && (
          <ActionsCell
            numeric
            name="actions"
            icon="more-dots"
            role={role}
            flex={3}
            setSelectedUser={setSelectedUser}
            setIsArchiving={setIsArchiving}
            setArchiveMode={setArchiveMode}
          />
        )}
      </UsersTable>

      <ArchiveDialog
        show={isArchiving}
        archiveMode={archiveMode}
        onConfirm={onConfirmArchive}
        onCancel={() => setIsArchiving(false)}
      />
    </>
  );
};

const AircraftCell = ({ item: user = {}, role = '', flex }) => {
  const { data = [] } = useUserAircraft({ id: user?.id, role });

  return (
    <Cell flex={flex}>
      {_.isEmpty(data) && '-'}
      {_.map(data, craft => (
        <>
          {craft.tailNumber}
          <br />
        </>
      ))}
    </Cell>
  );
};

const OwnersCell = ({ item: user = {}, owners, flex }) => {
  const userOwners = user?.ownerIds?.map(id => {
    const owner = _.find(owners, o => o.id === id);
    return `${owner?.firstName} ${owner?.lastName}`;
  });

  return (
    <Cell flex={flex}>
      {_.isEmpty(userOwners)
        ? '-'
        : userOwners.map(u => (
            <>
              {u}
              <br />
            </>
          ))}
    </Cell>
  );
};

const NameLinkCell = ({ item, ...cellProps }) => {
  const name = `${item.firstName} ${item.lastName}`;
  const labelStyle = { marginHorizontal: 0 };
  const destination = `${getRoleString(
    item.role === UserRole.EMPLOYEE ? UserRole.MANAGER : item.role,
  )} Details`;
  return (
    <Cell {...cellProps}>
      <Box dir="row" ai="center">
        <UserRoleIndicator user={item} />
        <LinkButton
          toScreen={destination}
          params={{
            userId: item.id,
            title: name,
          }}
          mode="text"
          labelStyle={labelStyle}>
          {name}
        </LinkButton>
      </Box>
    </Cell>
  );
};

const WeightCell = ({ item: user, flex }) => {
  return <Cell flex={flex}>{user?.weight ? `${user.weight} lbs` : '-'}</Cell>;
};

const ActionsCell = ({
  item: user,
  role,
  setSelectedUser,
  setIsArchiving,
  setArchiveMode,
  ...cellProps
}) => {
  const { open, isOpen, close } = useMenuState();
  const { actions, onAction } = useActions(user, role);

  const action = useAsyncCallback(userAction => {
    close();

    if (userAction === 'View') {
      return onAction({ action: userAction, user });
    }

    setSelectedUser(user);
    setIsArchiving(true);
    setArchiveMode(
      userAction === 'Archive'
        ? 'archive'
        : userAction === 'Restore'
        ? 'restore'
        : 'delete',
    );
  });

  const anchor = (
    <Button
      mode="text"
      icon="more-dots"
      loading={action.loading}
      onPress={open}
    />
  );

  return (
    <Cell {...cellProps}>
      <Menu anchor={anchor} visible={isOpen} onDismiss={close}>
        {_.map(actions, title => (
          <Menu.Item
            key={title}
            title={title}
            onPress={() => action.execute(title)}
          />
        ))}
      </Menu>
    </Cell>
  );
};

const useActions = (user, role) => {
  const { navigate } = useNavigation();
  const userId = getUid();

  const onAction = useCallback(
    async ({ action }) => {
      if (action === 'View') {
        const destination = `${getRoleString(
          user.role === UserRole.EMPLOYEE ? UserRole.MANAGER : user.role,
        )} Details`;
        navigate(destination, {
          userId: user.id,
          title: `${user.firstName} ${user.lastName}`,
        });
      }
    },
    [navigate, user],
  );

  const actions = ['View'];

  if (user.id !== userId) {
    if (user.archived) {
      actions.push('Restore', 'Delete');
    } else {
      actions.push('Archive');
    }
  }

  return {
    actions,
    onAction,
  };
};

type UserListProps = {
  role: 'owner' | 'employee' | 'manager' | 'pilot',
  withActions?: boolean,
  showArchived?: boolean,
};

export default UserList;
